/**
 * Content types for the Needs Map
 * 各トピックの内容をノードとして表現するための型定義
 */

import { Status } from './nodeTypes';

/**
 * コンテンツノード
 * トピックの具体的な内容を表すノード
 */
export interface ContentNode {
  id: string;          // コンテンツノードの一意のID
  topicId: string;     // 関連するTopicNodeのID
  content: string;     // 内容テキスト
  timestamp: string;   // 会話内のタイムスタンプ（HH:MM:SS形式）
  timestampSeconds: number; // タイムスタンプの秒数表現（ソート用）
  status: Status;      // 処理状態
  position?: {         // レイアウト上の位置
    x: number;
    y: number;
  };
  width?: number;      // 表示幅
  height?: number;     // 表示高さ
}

/**
 * ContentNodeの生成関数
 */
export function createContentNode(
  topicId: string,
  content: string,
  timestamp: string,
  id?: string
): ContentNode {
  // タイムスタンプを秒に変換
  const timestampSeconds = timestampToSeconds(timestamp);
  
  return {
    id: id || `content-${topicId}-${Date.now()}`,
    topicId,
    content,
    timestamp,
    timestampSeconds,
    status: 'success',
    width: 250,
    height: 100
  };
}

/**
 * ContentNode用のReactFlowデータ型
 */
export interface ContentNodeData {
  topicId: string;
  content: string;
  timestamp: string;
  timestampSeconds: number;
  status: Status;
  width?: number;
  height?: number;
}

/**
 * タイムスタンプ文字列を秒数に変換
 */
export function timestampToSeconds(timestamp: string): number {
  const parts = timestamp.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
}

/**
 * 秒数をタイムスタンプ文字列に変換
 */
export function secondsToTimestamp(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    secs.toString().padStart(2, '0')
  ].join(':');
}

/**
 * 秒数から視覚的なタイムライン上の位置を計算
 */
export function secondsToPosition(seconds: number, totalDuration: number, availableWidth: number): number {
  if (totalDuration <= 0) return 0;
  const ratio = seconds / totalDuration;
  return Math.floor(ratio * availableWidth);
}

/**
 * トピックノードからコンテンツノードの配列を生成
 */
export function generateContentNodesFromTopic(topicNode: any): ContentNode[] {
  if (!topicNode || !topicNode.contents || !Array.isArray(topicNode.contents)) {
    return [];
  }
  
  return topicNode.contents.map((content: any, index: number) => 
    createContentNode(
      topicNode.id,
      content.content,
      content.timestamp,
      `content-${topicNode.id}-${index}`
    )
  );
}